<template>
  <div
    id="display-properties-sidebar"
    class="h-100 bg-light border-left d-flex overflow-hidden"
  >
    <div
      id="icons"
      :class="['flex-shrink-0 d-flex flex-column align-items-center', {'border-right': isSidebarOpen}]"
    >
      <b-button
        id="sidebar-toggle-btn"
        data-test="sidebar-toggle-btn"
        variant="tertiary"
        class="tab-icon d-flex align-items-center justify-content-center"
        @click="toggleSidebarOpen('')"
        @mouseover="setTooltipTarget('sidebar-toggle-btn')"
        @mouseleave="setTooltipTarget('')"
        :disabled="!canToggleSidebar"
      >
        <i :class="`icon fal ${isSidebarOpen ? 'fa-chevron-double-right' : 'fa-chevron-double-left'}`" />
      </b-button>
      <div
        v-for="(section, index) in getTabs"
        :key="index"
        class="w-100"
      >
        <section
          v-if="section.length > 0"
          class="d-flex flex-column align-items-center"
        >
          <b-button
            v-for="(tab) in section"
            :key="tab.id"
            :id="tab.id"
            variant="tertiary"
            :class="['tab-icon d-flex align-items-center justify-content-center', {'tab-active' : getActiveTab === tab.id}]"
            @click="toggleSidebarOpen(tab.id)"
            @mouseover="setTooltipTarget(tab.id)"
            @mouseleave="setTooltipTarget('')"
            :disabled="!canToggleSidebar"
            :data-test="tab.id"
          >
            <i
              :class="`icon ${getTabInfo(tab.id, 'icon')}`"
            />
          </b-button>
        </section>
        <hr
          v-if="getTabs.length > 1 && section.length > 0 && index !== getTabs.length -1"
          class="my-2"
        >
      </div>
    </div>
    <div
      id="display-properties-content-container"
      ref="display-properties-content-container"
      class="bg-white flex-grow-1"
      :class="{'is-open': isSidebarOpen}"
      data-test="display-properties-content-container"
    >
      <div
        id="display-properties-content"
        class="h-100 p-4"
      >
        <component
          :is="getTabInfo(getActiveTab, 'component')"
          :key="getTabInfo(getActiveTab, 'component')"
          :is-sidebar-open="isSidebarOpen"
        />
      </div>
    </div>
    <template
      v-if="tooltip.target !== '' && !tooltip.isDisabled"
    >
      <b-tooltip
        :key="tooltip.target"
        :target="tooltip.target"
        triggers="hover"
        placement="left"
      >
        <template
          v-if="tooltip.target === 'sidebar-toggle-btn'"
        >
          {{ `${!isSidebarOpen ? 'Open' : 'Close'} Sidebar` }}
        </template>
        <template
          v-else
        >
          {{ getTabInfo(tooltip.target, 'tooltip') }}
        </template>
      </b-tooltip>
    </template>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import * as contentTypes from '@/modules/content/store/types';

import Comments from '@/modules/showcase/components/Sidebar/Comments';

export default {
  name: 'DisplayPropertiesSidebar',
  components: {
    Comments,
  },
  props: {
    isSidebarOpen: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      activeTabId: '',
      transitionTimer: 0.5,
      tooltip: {
        isDisabled: false,
        target: '',
      },
    };
  },
  computed: {
    ...mapState('content', [
      'selectedArticleUuid',
    ]),
    ...mapGetters('content', [
      contentTypes.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS,
    ]),
    getActiveTab() {
      return 'creative-template-comments';
    },
    getCommentsManagementTabs() {
      const tabs = [
        {
          id: 'creative-template-comments',
          icon: 'fal fa-comments-alt',
          tooltip: 'Comments',
          component: 'Comments',
        },
      ];
      return tabs;
    },
    getTabs() {
      return [
        this.getCommentsManagementTabs,
      ];
    },
    hasArticles() {
      return this.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS.length > 0;
    },
    canToggleSidebar() {
      return this.hasArticles
        && this.selectedArticleUuid;
    },
  },
  methods: {
    getTabInfo(tabId, info) {
      if (!tabId) return;
      return this.getTabs.flat().find((tab) => tab.id === tabId)[info];
    },
    async toggleSidebarOpen(activeTabId) {
      if (!activeTabId) {
        this.$emit('setIsSidebarOpen', !this.isSidebarOpen);
        return;
      }
      this.tooltip.isDisabled = true;
      if (this.isSidebarOpen && this.getActiveTab !== activeTabId) this.$emit('setIsSidebarOpen', true);
      else this.$emit('setIsSidebarOpen', !this.isSidebarOpen);
      this.activeTabId = activeTabId;
      setTimeout(() => {
        this.tooltip.isDisabled = false;
      }, this.transitionTimer * 1000);
    },
    setTooltipTarget(targetId) {
      this.$set(this.tooltip, 'target', targetId);
    },
  },
  async mounted() {
    const displayPropertiesContent = this.$refs['display-properties-content-container'];
    if (!displayPropertiesContent) await this.$nextTick();
    displayPropertiesContent.style.setProperty('transition', `width ${this.transitionTimer}s`);
  },
};
</script>
<style scoped lang="scss">
$display-properties-content-width: 400px;
#display-properties-content-container {
  width: 0px;
  &.is-open {
    width: $display-properties-content-width;
  }
}
#display-properties-content {
  overflow-y: auto;
  width: $display-properties-content-width;
}
#icons {
  width: 75px !important;
}
.tab-icon {
  border: unset;
  padding: 1rem;
  cursor: pointer;
  &:focus {
    border: unset;
    outline: none;
    box-shadow: none;
  }
  &:hover:not(.disabled) {
    color: $link;
  }
  &.tab-active {
    color: $link-dark;
  }
}
.icon {
  color: inherit;
  font-size: 1.5rem;
}
hr {
  padding: 0;
  width: 100%;
  height: 1px;
}
</style>
